<template>
    <div class="sc-connection">
        <!-- Status wird geladen -->
        <div v-if="statusLoading" class="d-flex py-6">
            <v-progress-circular indeterminate color="primary" class="mx-auto"></v-progress-circular>
        </div>

        <!-- Server ohne Google-Zugangsdaten -->
        <div v-else-if="status && status.configured === false" class="qgreycard pa-5" style="font-size: 14px">
            <v-icon class="mr-2" color="grey">mdi-alert-circle-outline</v-icon>
            {{ describe("not_configured") }}
        </div>

        <!-- Nicht verbunden -->
        <div v-else-if="!connected" class="qgreycard pa-6 text-center">
            <v-icon large color="primary" class="mb-2">mdi-google</v-icon>
            <h3 style="font-weight: 300">Connect Google Search Console</h3>
            <p class="mt-2 mb-4 mx-auto" style="max-width: 520px; font-size: 14px">
                <template v-if="status && status.reason === 'reauth_required'">{{ describe("reauth_required") }}</template>
                <template v-else>{{ intro }}</template>
            </p>
            <v-btn v-if="canManage" rounded color="primary" :loading="connecting" @click="connect">
                <v-icon class="mr-2" small>mdi-google</v-icon>
                Connect with Google
            </v-btn>
            <div v-else style="font-size: 13px; opacity: 0.8">Ask an admin of your company to connect Google Search Console.</div>
            <div v-if="errorMessage" class="mt-3 error--text" style="font-size: 13px">{{ errorMessage }}</div>
        </div>

        <!-- Verbunden -->
        <div v-else>
            <div class="d-flex align-center flex-wrap mb-2" style="font-size: 13px">
                <v-icon small color="primary" class="mr-1">mdi-check-circle</v-icon>
                <span class="font-weight-bold mr-2">Google Search Console connected</span>
                <span v-if="connectedSince" class="mr-2" style="opacity: 0.7">since {{ connectedSince }}</span>
                <span class="mr-2" style="opacity: 0.7">&middot; {{ sites.length }} {{ sites.length === 1 ? "property" : "properties" }}</span>
                <v-spacer></v-spacer>
                <v-btn x-small icon :disabled="statusLoading" @click="fetchStatus" title="Refresh properties" aria-label="Refresh properties"><v-icon small>mdi-refresh</v-icon></v-btn>
                <template v-if="canManage">
                    <v-btn v-if="!confirmDisconnect" x-small text color="grey" @click="confirmDisconnect = true">disconnect</v-btn>
                    <template v-else>
                        <span class="mr-1">Disconnect Google?</span>
                        <v-btn x-small text color="error" :loading="disconnecting" @click="disconnect">yes</v-btn>
                        <v-btn x-small text @click="confirmDisconnect = false">no</v-btn>
                    </template>
                </template>
            </div>
            <div v-if="errorMessage" class="mb-2 error--text" style="font-size: 13px">{{ errorMessage }}</div>
            <div v-if="showProperties" class="mb-3">
                <div v-if="!sites.length" style="font-size: 13px">{{ viaApi ? "No properties are enabled for Quaro yet." : "Your Google account has no verified Search Console properties." }}</div>
                <v-chip v-for="site in sites" :key="site.site_url" small outlined class="mr-1 mb-1" :title="site.permission_level">
                    <v-icon x-small class="mr-1">{{ site.site_url.indexOf("sc-domain:") === 0 ? "mdi-domain" : "mdi-link-variant" }}</v-icon>
                    {{ site.site_url }}
                </v-chip>
            </div>
            <!-- Nur ueber die searchinsights-API (VUE_APP_GSC_VIA_API): Admins waehlen, welche Properties Quaro abfragen darf -->
            <div v-if="viaApi && canManage" class="mb-3">
                <SearchConsoleProperties @saved="fetchStatus" @notify="forwardNotify"></SearchConsoleProperties>
            </div>
            <slot :sites="sites" :status="status"></slot>
        </div>
    </div>
</template>

<script>
    // Verbindung eines Kunden zur Google Search Console: Status, Verbinden (OAuth über die
    // Monitoring-API), Trennen. Benutzt in den Einstellungen (IntegrationsSettings.vue); der Slot
    // ist für Aufrufer gedacht, die im verbundenen Zustand mit den Properties weiterarbeiten.
    // Die Verbindung gilt für den ganzen Kunden, nicht für einen einzelnen Nutzer.
    //
    // Events:
    //   statusChanged(status)       nach jedem Statuswechsel ({ connected, sites, reason, ... })
    //   connecting                  direkt vor dem Sprung zum Google-Login (Entwürfe sichern!)
    //   notify(message, icon, color)
    import { getMonitoringBaseUrl } from "../utils/aiSuiteProjectSettings"
    import { describeSearchConsoleError, searchConsoleErrorCode, searchConsoleReturnUrl, searchConsoleViaApi, statusFromGscApi } from "../utils/searchConsole"
    import SearchConsoleProperties from "./SearchConsoleProperties"

    export default {
        name: "SearchConsoleConnection",
        components: { SearchConsoleProperties },
        props: {
            // Liste der Properties im verbundenen Zustand anzeigen (Einstellungen)
            showProperties: Boolean,
            // Erklärtext über dem Connect-Button
            intro: {
                type: String,
                default: "Connect the Google account that has access to your Search Console properties. Quaro only reads your Search Console data and never changes anything in your Google account."
            }
        },
        data() {
            return {
                statusLoading: false,
                status: null,
                connecting: false,
                disconnecting: false,
                confirmDisconnect: false,
                errorMessage: ""
            }
        },
        computed: {
            baseUrl() {
                return getMonitoringBaseUrl({ companyIdEncrypted: localStorage.company_id, cryptoJS: this.CryptoJS })
            },
            // true: alles läuft über die searchinsights-API (/gsc/*) mit Session-Prüfung und Admin-Rechten
            viaApi() {
                return searchConsoleViaApi()
            },
            // Verbinden/Trennen/Freigeben darf nur ein Admin - nur über die API prüfbar, im bisherigen Modus bleibt es wie vorher
            canManage() {
                return !this.viaApi || !this.status || this.status.can_manage !== false
            },
            connected() {
                return Boolean(this.status && this.status.connected)
            },
            sites() {
                return (this.status && this.status.sites) || []
            },
            connectedSince() {
                const at = this.status && this.status.connected_at
                if (!at) return ""
                const date = new Date(at)
                return isNaN(date.getTime()) ? "" : date.toLocaleDateString()
            }
        },
        methods: {
            describe(code) {
                return describeSearchConsoleError(code)
            },
            post(path, body) {
                return this.$helpers.axiosPost(this.baseUrl + "/searchconsole" + path, { company_id: localStorage.company_id, ...body })
            },
            // searchinsights-API: Session-Token + Firma + Rolle werden dort serverseitig geprüft (wie die 2FA-Einstellungen)
            gscPost(path, body) {
                return this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/gsc" + path, { user_id: localStorage.id, company_id: localStorage.company_id, token: localStorage.token, ...body })
            },
            forwardNotify(message, icon, color) {
                this.$emit("notify", message, icon, color)
            },
            async fetchStatus() {
                this.statusLoading = true
                this.errorMessage = ""
                try {
                    if (this.viaApi) {
                        this.status = statusFromGscApi((await this.gscPost("/status", {})).data)
                    } else {
                        const response = await this.post("/status", {})
                        this.status = response.data
                    }
                } catch (error) {
                    const code = searchConsoleErrorCode(error)
                    this.status = { connected: false, configured: true, reason: code === "reauth_required" ? "reauth_required" : null }
                    if (code !== "reauth_required") this.errorMessage = this.describe(code)
                } finally {
                    this.statusLoading = false
                    this.$emit("statusChanged", this.status)
                }
            },
            // Von außen aufrufbar, wenn eine Datenabfrage mit reauth_required/not_connected scheitert.
            markDisconnected(reason) {
                this.status = { connected: false, configured: true, reason: reason || null }
                this.$emit("statusChanged", this.status)
            },
            async connect() {
                this.connecting = true
                this.errorMessage = ""
                try {
                    // Der Google-Login verlässt die Seite - Aufrufer sichern vorher ihren Zustand.
                    this.$emit("connecting")
                    // Über die API: Ticket nach Admin-Prüfung, dann Sprung zum Start der Anmeldung. Der Rücksprung geht auf eine feste
                    // Adresse (Einstellungen > Integrations), return_to gibt es dort nicht mehr.
                    const response = this.viaApi ? await this.gscPost("/connect", {}) : await this.post("/auth/google", { return_to: searchConsoleReturnUrl() })
                    const target = this.viaApi ? response.data && response.data.start_url : response.data && response.data.url
                    if (target) {
                        window.location.assign(target)
                        return
                    }
                    this.errorMessage = this.describe(searchConsoleErrorCode(response))
                } catch (error) {
                    this.errorMessage = this.describe(searchConsoleErrorCode(error))
                }
                this.connecting = false
            },
            async disconnect() {
                this.disconnecting = true
                this.errorMessage = ""
                try {
                    await (this.viaApi ? this.gscPost("/disconnect", {}) : this.post("/disconnect", {}))
                    this.confirmDisconnect = false
                    this.status = { connected: false, configured: true, reason: null, can_manage: true }
                    this.$emit("statusChanged", this.status)
                    this.$emit("notify", "Google Search Console disconnected.", "mdi-check-circle", "primary")
                } catch (error) {
                    this.errorMessage = this.describe(searchConsoleErrorCode(error))
                }
                this.disconnecting = false
            }
        },
        mounted() {
            this.fetchStatus()
        }
    }
</script>

<style scoped></style>
